import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Card, Toast, ToastContainer } from "react-bootstrap";
import CustomNavbar from "../CustomNavbar";
import "./css/ZRQRReader.css";
import cfg from "../../config";

const ZRQRReader = () => {
  const [toastList, setToastList] = useState([]);
  const scannerRef = useRef(null);
  const isHttps = window.location.protocol === "https:" || window.location.hostname === "localhost";

  // Map zum Tracken von Codes, die gerade gescannt werden
  const processingRef = useRef({});

  const handleDecodedQr = async (decodedText) => {
    if (processingRef.current[decodedText]) return; // Bereits in Bearbeitung

    let qrData;
    try {
      qrData = JSON.parse(decodedText);
    } catch {
      showToast("Ungültiger QR-Code.", "error");
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      showToast("Kein Token vorhanden. Bitte einloggen.", "error");
      return;
    }

    processingRef.current[decodedText] = true; // Code wird verarbeitet

    try {
      const response = await fetch("/api/movebookings/confirm", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          bookingID: qrData.bookingID,
          phase: qrData.phase,
        }),
      });

      const result = await response.json();

      let type = "success";
      if (!response.ok) type = "error";
      else if (result.message.toLowerCase().includes("bereits bestätigt")) type = "warning";

      showToast(
        <div>
          <strong>{qrData.memberName}</strong> – {cfg.costumeTerm} #{qrData.haesNumber} ({qrData.typ})<br />
          <span style={{ fontStyle: "italic" }}>{result.message}</span>
        </div>,
        type
      );

      // Event auslösen für MyQrCode.jsx zum Aktualisieren
      if (response.ok && !result.message.toLowerCase().includes("bereits bestätigt")) {
        window.dispatchEvent(new Event("qrCodeConfirmed"));
      }

    } catch (err) {
      showToast("Fehler beim Verbinden mit dem Server.", "error");
    } finally {
      // Nach 3 Sekunden wieder freigeben, damit der Code erneut gescannt werden kann
      setTimeout(() => {
        delete processingRef.current[decodedText];
      }, 3000);
    }
  };

  const showToast = (content, type) => {
    const id = Date.now();
    setToastList(prev => [...prev, { id, content, type }]);
    setTimeout(() => {
      setToastList(prev => prev.filter(t => t.id !== id));
    }, 5000); // 5 Sekunden sichtbar
  };

  const getToastVariant = (type) => {
    switch (type) {
      case "success": return "success";
      case "warning": return "warning";
      case "error": return "danger";
      default: return "info";
    }
  };

  useEffect(() => {
    if (!isHttps) {
      showToast("QR-Scanner funktioniert nur über HTTPS oder localhost.", "error");
      return;
    }

    const qrReaderElement = document.getElementById("qr-reader");
    if (!qrReaderElement) {
      showToast("QR-Reader-Element nicht gefunden.", "error");
      return;
    }

    const initScanner = async () => {
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (!cameras || cameras.length === 0) {
          showToast("Keine Kamera gefunden.", "error");
          return;
        }

        console.log("Gefundene Kameras:", cameras);

        // 1x-Rückkamera bevorzugen, alles andere 0,5x ignorieren
        let cameraId = cameras.find(cam => {
          const label = cam.label.toLowerCase();
          return (label.includes("back") || label.includes("rück")) && !label.includes("0.5");
        })?.id;

        if (!cameraId) {
          cameraId = cameras.find(cam => {
            const label = cam.label.toLowerCase();
            return label.includes("back") || label.includes("rück");
          })?.id;
        }

        if (!cameraId) cameraId = cameras[0].id;

        const html5Qrcode = new Html5Qrcode("qr-reader");
        scannerRef.current = html5Qrcode;

        await html5Qrcode.start(
          cameraId,
          { fps: 10, qrbox: 250, aspectRatio: 1 },
          handleDecodedQr,
          (error) => {
            if (error && !error.message.includes("No MultiFormat Readers")) {
              console.warn("Scan failed:", error);
            }
          }
        );

        showToast("Kamera erfolgreich gestartet", "success");

      } catch (err) {
        console.error("QR Scanner Error:", err);
        showToast("Fehler beim Zugriff auf die Kamera oder beim Scannen.", "error");
      }
    };

    initScanner();

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current.clear().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [isHttps]);

  return (
    <div>
      <CustomNavbar />
      <div className="container mt-3 d-flex flex-column align-items-center">
        <h4 className="mb-3 text-center">QR-Code scannen</h4>

        <Card className="shadow-lg p-3 w-100 qr-card">
          <div id="qr-reader" className="qr-reader-box"></div>
        </Card>

        <p className="mt-3 text-muted text-center small">
          Halte den QR-Code in den Rahmen, um ihn automatisch zu scannen.
        </p>

        <ToastContainer className="p-3 toast-container" position="top-center">
          {toastList.map(toast => (
            <Toast
              key={toast.id}
              bg={getToastVariant(toast.type)}
              className="slide-down-toast"
            >
              <Toast.Body className="text-center text-white">
                {toast.content}
              </Toast.Body>
            </Toast>
          ))}
        </ToastContainer>
      </div>
    </div>
  );
};

export default ZRQRReader;
